import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { isSubscriptionGrantingAccess } from '@/features/subscription/lib/trial-helpers';
import type {
  SubscriptionResource,
  Plan,
  PlanResource,
} from '@/types/subscription';

// Limits the API pre-seeds for guests + subscribers (used in scaffolded
// upsell copy). Kept as a discrete slice on the Query payload so guest /
// paid resolution is server-driven.
export interface EffectivePlan {
  name: string;
  slug: string;
  limit_projects: number;
  limit_angles: number;
  limit_references: number;
  limit_simultaneous_references: number;
  limit_generations_queue: number;
}

// Usage interface without token_balance (balance lives in cacheKeys.balance).
export interface Usage {
  projects_count?: number;
  renders_this_month?: number;
  subscription?: {
    monthly_token_allocation?: number;
    current_period_start?: string;
    current_period_end?: string;
  };
}

/**
 * Single bundle that backs the user's effective subscription state.
 * Written by `profileService.applyProfileData` and the WS `users.ts`
 * handler via `setQueryData(cacheKeys.subscriptions.active, …)`.
 */
export interface ActiveSubscriptionData {
  subscription: SubscriptionResource | null;
  plan: Plan | PlanResource | null;
  usage: Usage | null;
  isGuestMode: boolean;
  effectivePlan: EffectivePlan | null;
}

export const ACTIVE_SUBSCRIPTION_DEFAULT: ActiveSubscriptionData = {
  subscription: null,
  plan: null,
  usage: null,
  // Guest is the safe default before the profile has been seeded — matches
  // the prior Zustand initial state.
  isGuestMode: true,
  effectivePlan: null,
};

/**
 * Non-React reader for callers like `useLimitErrorPaywall` that need the
 * latest payload synchronously. Returns
 * `ACTIVE_SUBSCRIPTION_DEFAULT` when nothing has been seeded yet.
 *
 * The `useActiveSubscription` React hook lives in `subscription.selectors.ts`
 * to keep this file free of auth-store imports (cycle break:
 * profile.service → active-subscription.query was forming a loop via
 * auth.store).
 */
export function getActiveSubscriptionSnapshot(): ActiveSubscriptionData {
  return (
    queryClient.getQueryData<ActiveSubscriptionData>(
      cacheKeys.subscriptions.active
    ) ?? ACTIVE_SUBSCRIPTION_DEFAULT
  );
}

/**
 * Compute `isGuestMode` for the active-subscription bundle.
 *
 * Authority order:
 *  1. Explicit override (`explicit`) — used by callers that know the answer
 *     ahead of time.
 *  2. Server `user.can_generate` (`canGenerate`) when present — single
 *     source of truth that already folds in the post-signup welcome
 *     window. The welcome trial has no Subscription row, so the
 *     subscription-grants-access read below would classify a live trial
 *     as guest and block Generate behind the SUBSCRIPTION_REQUIRED
 *     paywall.
 *  3. Subscription `grants_access` — fallback for older API payloads that
 *     don't yet carry the user-level flag.
 */
export function deriveIsGuestMode(
  subscription: SubscriptionResource | null,
  explicit?: boolean,
  canGenerate?: boolean | null
): boolean {
  if (explicit !== undefined) return explicit;
  if (canGenerate !== undefined && canGenerate !== null) return !canGenerate;
  if (subscription === null) return true;
  const asResource = 'attributes' in subscription ? subscription : null;
  return !isSubscriptionGrantingAccess(asResource);
}
